/**
 * Lead Management Service
 * Handles CRUD operations for leads in the sales pipeline
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  Lead,
  LeadCreateData,
  LeadUpdateData,
  LeadFilters,
  LeadStats,
  LeadStatus,
  LeadActivity,
  ActivityType,
  EmailHistory,
  calculatePricingTier,
  calculateEstimatedValue,
  calculateLeadScore,
} from '../types/lead';
import { logAuditEvent } from './auditLog';
import { sendEmailWithFirebase } from './emailService';

const LEADS_COLLECTION = 'leads';

/**
 * Get a single lead by ID
 */
export const getLead = async (leadId: string): Promise<Lead | null> => {
  try {
    const leadDoc = await getDoc(doc(db, LEADS_COLLECTION, leadId));
    
    if (!leadDoc.exists()) {
      return null;
    }

    return {
      id: leadDoc.id,
      ...leadDoc.data(),
    } as Lead;
  } catch (error) {
    console.error('Error fetching lead:', error);
    throw new Error('Failed to fetch lead');
  }
};

/**
 * Get list of leads with filters
 */
export const getLeads = async (
  filters: LeadFilters = {}
): Promise<{ leads: Lead[]; total: number }> => {
  try {
    const constraints: QueryConstraint[] = [];

    // Apply filters
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }

    if (filters.source) {
      constraints.push(where('source', '==', filters.source));
    }

    if (filters.assignedTo) {
      constraints.push(where('assignedTo', '==', filters.assignedTo));
    }

    if (filters.pricingTier) {
      constraints.push(where('businessInfo.pricingTier', '==', filters.pricingTier));
    }

    // Apply sorting
    const sortField = filters.sortBy || 'createdAt';
    const sortDirection = filters.sortOrder || 'desc';
    constraints.push(orderBy(sortField, sortDirection));

    // Apply pagination
    if (filters.limit) {
      constraints.push(firestoreLimit(filters.limit));
    }

    const leadsQuery = query(collection(db, LEADS_COLLECTION), ...constraints);
    const snapshot = await getDocs(leadsQuery);

    let leads = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Lead[];

    // Apply client-side filters
    if (filters.minDoorCount) {
      leads = leads.filter(lead => lead.businessInfo.doorCount >= filters.minDoorCount!);
    }

    if (filters.maxDoorCount) {
      leads = leads.filter(lead => lead.businessInfo.doorCount <= filters.maxDoorCount!);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      leads = leads.filter(
        lead =>
          lead.contactInfo.name.toLowerCase().includes(searchLower) ||
          lead.contactInfo.email.toLowerCase().includes(searchLower) ||
          lead.contactInfo.company?.toLowerCase().includes(searchLower)
      );
    }

    return {
      leads,
      total: leads.length,
    };
  } catch (error) {
    console.error('Error fetching leads:', error);
    throw new Error('Failed to fetch leads');
  }
};

/**
 * Create a new lead
 */
export const createLead = async (
  data: LeadCreateData,
  createdBy: string
): Promise<Lead> => {
  try {
    const now = Timestamp.now();
    
    // Calculate pricing tier and estimated value
    const doorCount = data.businessInfo.doorCount || 0;
    const pricingTier = calculatePricingTier(doorCount);
    const estimatedValue = calculateEstimatedValue(doorCount);
    
    const leadData: Omit<Lead, 'id'> = {
      status: 'new',
      source: data.source,
      contactInfo: data.contactInfo,
      businessInfo: {
        ...data.businessInfo,
        doorCount,
        pricingTier,
        painPoints: data.businessInfo.painPoints || [],
      },
      score: 0, // Will be calculated below
      estimatedValue,
      probability: 10, // Default 10% for new leads
      activities: [],
      notes: data.notes,
      tags: data.tags || [],
      createdAt: now,
      updatedAt: now,
      createdBy,
    };

    // Calculate lead score
    leadData.score = calculateLeadScore(leadData);

    const docRef = await addDoc(collection(db, LEADS_COLLECTION), leadData);

    await logAuditEvent({
      action: 'lead.create',
      resource: 'leads',
      resourceId: docRef.id,
      details: {
        name: data.contactInfo.name,
        email: data.contactInfo.email,
        doorCount,
        source: data.source,
      },
      performedBy: createdBy,
    });

    return {
      id: docRef.id,
      ...leadData,
    };
  } catch (error) {
    console.error('Error creating lead:', error);
    throw new Error('Failed to create lead');
  }
};

/**
 * Update an existing lead
 */
export const updateLead = async (
  leadId: string,
  data: LeadUpdateData,
  updatedBy: string
): Promise<void> => {
  try {
    const updateData: any = {
      ...data,
      updatedAt: Timestamp.now(),
    };

    // Recalculate pricing tier if door count changed
    if (data.businessInfo?.doorCount) {
      updateData['businessInfo.pricingTier'] = calculatePricingTier(data.businessInfo.doorCount);
      updateData['estimatedValue'] = calculateEstimatedValue(data.businessInfo.doorCount);
    }

    // Recalculate lead score if relevant fields changed
    if (data.businessInfo) {
      const currentLead = await getLead(leadId);
      if (currentLead) {
        const updatedLead: Lead = {
          ...currentLead,
          ...data,
          businessInfo: {
            ...currentLead.businessInfo,
            ...data.businessInfo,
          },
        } as Lead;
        updateData.score = calculateLeadScore(updatedLead);
      }
    }

    await updateDoc(doc(db, LEADS_COLLECTION, leadId), updateData);

    await logAuditEvent({
      action: 'lead.update',
      resource: 'leads',
      resourceId: leadId,
      details: data,
      performedBy: updatedBy,
    });
  } catch (error) {
    console.error('Error updating lead:', error);
    throw new Error('Failed to update lead');
  }
};

/**
 * Add activity to lead timeline
 */
export const addLeadActivity = async (
  leadId: string,
  type: ActivityType,
  subject: string,
  description: string,
  performedBy: string,
  performedByName: string,
  metadata?: Record<string, any>
): Promise<void> => {
  try {
    const lead = await getLead(leadId);
    if (!lead) {
      throw new Error('Lead not found');
    }

    const activity: LeadActivity = {
      id: `activity_${Date.now()}`,
      type,
      subject,
      description,
      performedBy,
      performedByName,
      timestamp: Timestamp.now(),
      metadata: metadata || {},
    };

    // Filter out any undefined activities and add the new one
    const updatedActivities = [
      ...(Array.isArray(lead.activities) ? lead.activities.filter((a: any) => a !== undefined) : []),
      activity
    ];

    await updateDoc(doc(db, LEADS_COLLECTION, leadId), {
      activities: updatedActivities,
      updatedAt: Timestamp.now(),
    });

    await logAuditEvent({
      action: `lead.activity.${type}`,
      resource: 'leads',
      resourceId: leadId,
      details: { subject, type },
      performedBy,
    });
  } catch (error) {
    console.error('Error adding lead activity:', error);
    throw new Error('Failed to add lead activity');
  }
};

/**
 * Assign lead to sales rep
 */
export const assignLead = async (
  leadId: string,
  assignedTo: string,
  assignedToName: string,
  assignedBy: string
): Promise<void> => {
  try {
    await updateDoc(doc(db, LEADS_COLLECTION, leadId), {
      assignedTo,
      assignedToName,
      updatedAt: Timestamp.now(),
    });

    await addLeadActivity(
      leadId,
      'note',
      'Lead Assigned',
      `Lead assigned to ${assignedToName}`,
      assignedBy,
      'System'
    );

    await logAuditEvent({
      action: 'lead.assign',
      resource: 'leads',
      resourceId: leadId,
      details: { assignedTo, assignedToName },
      performedBy: assignedBy,
    });
  } catch (error) {
    console.error('Error assigning lead:', error);
    throw new Error('Failed to assign lead');
  }
};

/**
 * Update lead status
 */
export const updateLeadStatus = async (
  leadId: string,
  newStatus: LeadStatus,
  updatedBy: string,
  updatedByName: string,
  notes?: string
): Promise<void> => {
  try {
    const updateData: any = {
      status: newStatus,
      updatedAt: Timestamp.now(),
    };

    // Update probability based on status
    const statusProbability: Record<LeadStatus, number> = {
      new: 10,
      qualifying: 20,
      demo_scheduled: 40,
      demo_completed: 60,
      proposal_sent: 70,
      negotiating: 80,
      closed_won: 100,
      closed_lost: 0,
    };
    updateData.probability = statusProbability[newStatus];

    // Set timestamps for specific statuses
    if (newStatus === 'demo_completed') {
      updateData.demoCompletedAt = Timestamp.now();
    } else if (newStatus === 'proposal_sent') {
      updateData.proposalSentAt = Timestamp.now();
    } else if (newStatus === 'closed_won' || newStatus === 'closed_lost') {
      updateData.closedAt = Timestamp.now();
    }

    await updateDoc(doc(db, LEADS_COLLECTION, leadId), updateData);

    await addLeadActivity(
      leadId,
      'note',
      `Status Changed`,
      `Status updated to: ${newStatus}${notes ? `. Notes: ${notes}` : ''}`,
      updatedBy,
      updatedByName
    );

    await logAuditEvent({
      action: 'lead.status_change',
      resource: 'leads',
      resourceId: leadId,
      details: { newStatus, notes },
      performedBy: updatedBy,
    });
  } catch (error) {
    console.error('Error updating lead status:', error);
    throw new Error('Failed to update lead status');
  }
};

/**
 * Schedule demo for lead
 */
export const scheduleDemoForLead = async (
  leadId: string,
  demoDate: Timestamp,
  scheduledBy: string,
  scheduledByName: string,
  notes?: string
): Promise<void> => {
  try {
    await updateDoc(doc(db, LEADS_COLLECTION, leadId), {
      status: 'demo_scheduled',
      demoScheduledFor: demoDate,
      probability: 40,
      updatedAt: Timestamp.now(),
    });

    await addLeadActivity(
      leadId,
      'demo',
      'Demo Scheduled',
      `Demo scheduled for ${demoDate.toDate().toLocaleString()}${notes ? `. Notes: ${notes}` : ''}`,
      scheduledBy,
      scheduledByName
    );

    await logAuditEvent({
      action: 'lead.demo_scheduled',
      resource: 'leads',
      resourceId: leadId,
      details: { demoDate: demoDate.toDate().toISOString() },
      performedBy: scheduledBy,
    });
  } catch (error) {
    console.error('Error scheduling demo:', error);
    throw new Error('Failed to schedule demo');
  }
};

/**
 * Mark lead as closed won
 */
export const closeLeadAsWon = async (
  leadId: string,
  contractId: string,
  finalValue: number,
  closedBy: string,
  closedByName: string
): Promise<void> => {
  try {
    await updateDoc(doc(db, LEADS_COLLECTION, leadId), {
      status: 'closed_won',
      probability: 100,
      closedAt: Timestamp.now(),
      wonDetails: {
        contractId,
        finalValue,
      },
      updatedAt: Timestamp.now(),
    });

    await addLeadActivity(
      leadId,
      'note',
      'Deal Closed - Won!',
      `Contract signed. Annual value: $${finalValue.toLocaleString()}`,
      closedBy,
      closedByName
    );

    await logAuditEvent({
      action: 'lead.closed_won',
      resource: 'leads',
      resourceId: leadId,
      details: { contractId, finalValue },
      performedBy: closedBy,
    });
  } catch (error) {
    console.error('Error closing lead as won:', error);
    throw new Error('Failed to close lead as won');
  }
};

/**
 * Mark lead as closed lost
 */
export const closeLeadAsLost = async (
  leadId: string,
  reason: string,
  closedBy: string,
  closedByName: string
): Promise<void> => {
  try {
    await updateDoc(doc(db, LEADS_COLLECTION, leadId), {
      status: 'closed_lost',
      probability: 0,
      closedAt: Timestamp.now(),
      lostReason: reason,
      updatedAt: Timestamp.now(),
    });

    await addLeadActivity(
      leadId,
      'note',
      'Deal Closed - Lost',
      `Lost reason: ${reason}`,
      closedBy,
      closedByName
    );

    await logAuditEvent({
      action: 'lead.closed_lost',
      resource: 'leads',
      resourceId: leadId,
      details: { reason },
      performedBy: closedBy,
    });
  } catch (error) {
    console.error('Error closing lead as lost:', error);
    throw new Error('Failed to close lead as lost');
  }
};

/**
 * Get lead statistics
 */
export const getLeadStats = async (): Promise<LeadStats> => {
  try {
    const snapshot = await getDocs(collection(db, LEADS_COLLECTION));
    const leads = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Lead[];

    // Count by status
    const byStatus = leads.reduce((acc, lead) => {
      acc[lead.status] = (acc[lead.status] || 0) + 1;
      return acc;
    }, {} as Record<LeadStatus, number>);

    // Count by source
    const bySource = leads.reduce((acc, lead) => {
      acc[lead.source] = (acc[lead.source] || 0) + 1;
      return acc;
    }, {} as any);

    // Calculate averages
    const avgDoorCount = leads.length > 0
      ? leads.reduce((sum, lead) => sum + lead.businessInfo.doorCount, 0) / leads.length
      : 0;

    const avgScore = leads.length > 0
      ? leads.reduce((sum, lead) => sum + lead.score, 0) / leads.length
      : 0;

    // Pipeline values
    const activeleads = leads.filter(l => 
      !['closed_won', 'closed_lost'].includes(l.status)
    );
    
    const totalPipelineValue = activeleads.reduce(
      (sum, lead) => sum + lead.estimatedValue,
      0
    );

    const weightedPipelineValue = activeleads.reduce(
      (sum, lead) => sum + (lead.estimatedValue * lead.probability / 100),
      0
    );

    // Conversion rates
    const totalLeads = leads.length;
    const demosScheduled = leads.filter(l => 
      ['demo_scheduled', 'demo_completed', 'proposal_sent', 'negotiating', 'closed_won'].includes(l.status)
    ).length;
    const proposalsSent = leads.filter(l => 
      ['proposal_sent', 'negotiating', 'closed_won'].includes(l.status)
    ).length;
    const closedWon = leads.filter(l => l.status === 'closed_won').length;

    const conversionRates = {
      leadToDemo: totalLeads > 0 ? (demosScheduled / totalLeads) * 100 : 0,
      demoToProposal: demosScheduled > 0 ? (proposalsSent / demosScheduled) * 100 : 0,
      proposalToClose: proposalsSent > 0 ? (closedWon / proposalsSent) * 100 : 0,
      overallCloseRate: totalLeads > 0 ? (closedWon / totalLeads) * 100 : 0,
    };

    return {
      total: leads.length,
      byStatus,
      bySource,
      avgDoorCount,
      avgScore,
      totalPipelineValue,
      weightedPipelineValue,
      conversionRates,
    };
  } catch (error) {
    console.error('Error fetching lead stats:', error);
    throw new Error('Failed to fetch lead stats');
  }
};

/**
 * Get leads that need follow-up based on nextFollowupDate
 */
export const getLeadsNeedingFollowup = async (): Promise<Lead[]> => {
  try {
    const today = Timestamp.now();
    const leadsRef = collection(db, LEADS_COLLECTION);
    
    // Query leads where:
    // - nextFollowupDate <= today
    // - status not in closed states
    // - autoFollowupEnabled is true (or undefined for backwards compatibility)
    const constraints: QueryConstraint[] = [
      where('nextFollowupDate', '<=', today),
      where('status', 'not-in', ['closed_won', 'closed_lost']),
      orderBy('nextFollowupDate', 'asc'),
    ];
    
    const q = query(leadsRef, ...constraints);
    const snapshot = await getDocs(q);
    
    const leads = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as Lead))
      .filter(lead => lead.autoFollowupEnabled !== false); // Include if undefined or true
    
    return leads;
  } catch (error) {
    console.error('Error fetching leads needing followup:', error);
    throw new Error('Failed to fetch leads needing followup');
  }
};

/**
 * Mark leads that need follow-up (for manual review workflow)
 */
export const markLeadsNeedingFollowup = async (): Promise<number> => {
  try {
    const leads = await getLeadsNeedingFollowup();
    
    for (const lead of leads) {
      await updateDoc(doc(db, LEADS_COLLECTION, lead.id), {
        needsFollowup: true,
        updatedAt: Timestamp.now(),
      });
    }
    
    return leads.length;
  } catch (error) {
    console.error('Error marking leads needing followup:', error);
    throw new Error('Failed to mark leads needing followup');
  }
};

/**
 * Log email sent to lead
 */
export const logEmailSent = async (
  leadId: string,
  subject: string,
  body: string,
  sentBy: string,
  sentByName: string,
  generatedByAI: boolean = false,
  messageId?: string
): Promise<void> => {
  try {
    const leadRef = doc(db, LEADS_COLLECTION, leadId);
    const leadDoc = await getDoc(leadRef);
    
    if (!leadDoc.exists()) {
      throw new Error('Lead not found');
    }
    
    const lead = leadDoc.data() as Lead;
    const now = Timestamp.now();
    
    const emailEntry: EmailHistory = {
      id: `email_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      subject,
      body,
      sentAt: now,
      sentBy,
      sentByName,
      generatedByAI,
      status: 'sent',
      messageId,
    };
    
    const emailHistory = lead.emailHistory || [];
    emailHistory.push(emailEntry);
    
    // Also log as activity
    const activity: LeadActivity = {
      id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'email',
      subject: `Email sent: ${subject}`,
      description: generatedByAI ? 'AI-generated follow-up email' : 'Manual email',
      performedBy: sentBy,
      performedByName: sentByName,
      timestamp: now,
      metadata: { generatedByAI, messageId },
    };
    
    const activities = lead.activities || [];
    activities.push(activity);
    
    // Calculate next follow-up date (3 days from now)
    const nextFollowupDate = new Date();
    nextFollowupDate.setDate(nextFollowupDate.getDate() + 3);
    
    await updateDoc(leadRef, {
      emailHistory,
      activities,
      lastEmailSentAt: now,
      nextFollowupDate: Timestamp.fromDate(nextFollowupDate),
      needsFollowup: false,
      updatedAt: now,
    });
    
    // Log audit event
    await logAuditEvent({
      action: 'lead_email_sent',
      resource: 'lead',
      resourceId: leadId,
      details: {
        subject,
        generatedByAI,
      },
      performedBy: sentBy,
      performedByName: sentByName,
    });
  } catch (error) {
    console.error('Error logging email sent:', error);
    throw new Error('Failed to log email sent');
  }
};

/**
 * Send follow-up email to lead
 * Uses Firebase Trigger Email Extension for server-side email delivery
 */
export const sendFollowupEmail = async (
  leadId: string,
  subject: string,
  body: string,
  sentBy: string,
  sentByName: string,
  generatedByAI: boolean = false
): Promise<{ success: boolean; messageId?: string; error?: string }> => {
  try {
    // Get lead info for email address
    const lead = await getLead(leadId);
    if (!lead || !lead.contactInfo?.email) {
      throw new Error('Lead not found or email missing');
    }
    
    // Send email via Firebase extension
    const result = await sendEmailWithFirebase({
      to: lead.contactInfo.email,
      subject,
      body,
      from: 'noreply@dossiloc.ca',
      fromName: 'DossiLoc Sales Team',
      leadId,
      generatedByAI,
      sentBy,
      sentByName,
    });
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to send email');
    }
    
    // Log email to lead's history
    await logEmailSent(leadId, subject, body, sentBy, sentByName, generatedByAI, result.messageId);
    
    return {
      success: true,
      messageId: result.messageId,
    };
  } catch (error) {
    console.error('Error sending follow-up email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Clear needs follow-up flag
 */
export const clearNeedsFollowup = async (leadId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, LEADS_COLLECTION, leadId), {
      needsFollowup: false,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error clearing needs followup:', error);
    throw new Error('Failed to clear needs followup');
  }
};

/**
 * Set next follow-up date for a lead
 */
export const setNextFollowupDate = async (
  leadId: string,
  daysFromNow: number
): Promise<void> => {
  try {
    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + daysFromNow);
    
    await updateDoc(doc(db, LEADS_COLLECTION, leadId), {
      nextFollowupDate: Timestamp.fromDate(nextDate),
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error setting next followup date:', error);
    throw new Error('Failed to set next followup date');
  }
};

/**
 * Bulk update lead fields
 */
export const bulkUpdateLead = async (
  leadId: string,
  updates: LeadUpdateData,
  updatedBy: string
): Promise<void> => {
  try {
    const updateData: any = {
      ...updates,
      updatedAt: Timestamp.now(),
    };

    // Recalculate pricing tier if door count changed
    if (updates.businessInfo?.doorCount) {
      updateData['businessInfo.pricingTier'] = calculatePricingTier(updates.businessInfo.doorCount);
      updateData['estimatedValue'] = calculateEstimatedValue(updates.businessInfo.doorCount);
    }

    // Recalculate lead score if relevant fields changed
    if (updates.businessInfo || updates.contactInfo) {
      const currentLead = await getLead(leadId);
      if (currentLead) {
        const updatedLead: Lead = {
          ...currentLead,
          ...updates,
          businessInfo: {
            ...currentLead.businessInfo,
            ...updates.businessInfo,
          },
          contactInfo: {
            ...currentLead.contactInfo,
            ...updates.contactInfo,
          },
        } as Lead;
        updateData.score = calculateLeadScore(updatedLead);
      }
    }

    await updateDoc(doc(db, LEADS_COLLECTION, leadId), updateData);

    await logAuditEvent({
      action: 'lead.bulk_update',
      resource: 'leads',
      resourceId: leadId,
      details: updates,
      performedBy: updatedBy,
    });
  } catch (error) {
    console.error('Error bulk updating lead:', error);
    throw new Error('Failed to bulk update lead');
  }
};

/**
 * Get field change history for a lead
 */
export const getFieldChangeHistory = async (leadId: string): Promise<any[]> => {
  try {
    const auditRef = collection(db, 'auditLogs');
    const q = query(
      auditRef,
      where('resourceId', '==', leadId),
      where('resource', '==', 'leads'),
      orderBy('timestamp', 'desc'),
      firestoreLimit(100)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching field change history:', error);
    return [];
  }
};
